import { Fragment, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SettingsSection } from '@/features/settings/settings-section';
import { cn } from '@/lib/utils';

import { useAddNoteMutation } from './assistant-notes.query';
import { NoteItemForm } from './note-item-form';
import { NotesItemRow } from './notes-item-row';

import type { NoteItem } from './assistant-notes.types';

type NotesItemListProps = {
  items: NoteItem[];
  className?: string;
  wrapperClassName?: string;
};

const withoutForm = (forms: ReadonlyMap<number, NoteItem>, number: number) =>
  new Map([...forms].filter(([openNumber]) => openNumber !== number));

/**
 * The notes of a converted store, in the frame the text area had: the rows scroll in the area
 * of the text, the add form opens at the end where the new note will appear, and the footer
 * holds Add note.
 */
export const NotesItemList = ({
  items,
  className,
  wrapperClassName,
}: NotesItemListProps) => {
  const [adding, setAdding] = useState(false);
  // The notes whose edit form is open, as they were when it opened. A form stays with its typed
  // text until the user saves or discards it, also when a refetch no longer lists its note
  const [openForms, setOpenForms] = useState<ReadonlyMap<number, NoteItem>>(
    new Map()
  );
  const rows = [
    ...items.filter((item) => !openForms.has(item.number)),
    ...openForms.values(),
  ].sort((first, second) => first.number - second.number);
  const addNote = useAddNoteMutation();

  return (
    <YStack
      className={cn(
        'w-full mt-2 rounded-lg border border-border-strong focus-within:border-accent overflow-hidden bg-surface',
        wrapperClassName
      )}
    >
      <YStack
        className={cn(className, 'min-h-0 gap-4 overflow-y-auto px-3 py-3')}
      >
        {rows.length === 0 && !adding && (
          <p className="text-sm text-foreground-muted">
            No notes yet. Littlebird adds notes as you chat, or add one
            yourself.
          </p>
        )}
        <YStack role="list" aria-label="Assistant notes" className="gap-4">
          {rows.map((item, index) => (
            <Fragment key={item.number}>
              {index > 0 && <SettingsSection.Seperator />}
              <NotesItemRow
                item={item}
                editing={openForms.has(item.number)}
                onStartEditing={() =>
                  setOpenForms((forms) => new Map(forms).set(item.number, item))
                }
                onStopEditing={() =>
                  setOpenForms((forms) => withoutForm(forms, item.number))
                }
              />
            </Fragment>
          ))}
        </YStack>
        {adding && (
          <NoteItemForm
            label="New note"
            submitLabel="Add"
            isPending={addNote.isPending}
            onSubmit={(text) =>
              addNote.mutate(text, { onSuccess: () => setAdding(false) })
            }
            onCancel={() => setAdding(false)}
          />
        )}
      </YStack>
      <XStack className="w-full justify-end border-t border-border-strong bg-surface-hover px-3 py-2">
        <Button
          variant="outline"
          size="dense"
          className="gap-1.5"
          disabled={adding}
          onClick={() => setAdding(true)}
        >
          <Icons.plus className="size-3.5" />
          Add note
        </Button>
      </XStack>
    </YStack>
  );
};
